import type { ActivityModule, ActivityHandle, ConclusaoExtras, Publico } from './activity';
import { audioManager } from './audio';
import { recordCompletion, getHighestLevel } from './score';
import { mascotManager } from './mascot';
import { isEmbed } from './embed';
import { reportar, ponte } from 'es-jogos';

interface InitOptions {
  root: HTMLElement;
  activities: ActivityModule[];
  renderHub(el: HTMLElement): void;
  renderHubAdulto(el: HTMLElement): void;
}

class Router {
  private root!: HTMLElement;
  private activities!: ActivityModule[];
  private renderHub!: (el: HTMLElement) => void;
  private renderHubAdulto!: (el: HTMLElement) => void;
  private current: ActivityHandle | null = null;
  private nivelInicioMs = 0;
  /**
   * MODO do público (F2, P-166): entrar por #/adulto/... liga 'adulto' e o modo
   * persiste nas navegações internas (os módulos montam hash sem o prefixo, ex.
   * "Próximo nível" → /algebra_plus/4). Voltar ao hub criança (#/) desliga.
   */
  private publico: Publico = 'crianca';

  init(opts: InitOptions): void {
    this.root = opts.root;
    this.activities = opts.activities;
    this.renderHub = opts.renderHub;
    this.renderHubAdulto = opts.renderHubAdulto;
    window.addEventListener('hashchange', () => this.handle());
    this.handle();
  }

  go(path: string): void {
    if (window.location.hash === `#${path}`) {
      this.handle();
    } else {
      window.location.hash = path;
    }
  }

  goHome(): void {
    this.go(this.publico === 'adulto' ? '/adulto' : '/');
  }

  private clear(): void {
    if (this.current) {
      this.current.unmount();
      this.current = null;
    }
    this.root.innerHTML = '';
  }

  private handle(): void {
    this.clear();
    const hash = window.location.hash.replace(/^#/, '') || '/';
    const parts = hash.split('/').filter(Boolean);

    // Prefixo /adulto liga o modo; hub criança (#/) desliga
    if (parts[0] === 'adulto') {
      this.publico = 'adulto';
      parts.shift();
    } else if (parts.length === 0) {
      this.publico = 'crianca';
    }
    mascotManager.setModoAdulto(this.publico === 'adulto');

    if (parts.length === 0) {
      if (this.publico === 'adulto') {
        this.renderHubAdulto(this.root);
      } else {
        this.renderHub(this.root);
      }
      return;
    }

    const [activityId, levelStr] = parts;
    const activity = this.activities.find((a) => a.id === activityId);
    if (!activity) {
      this.renderHub(this.root);
      return;
    }

    const level = levelStr ? Math.max(1, parseInt(levelStr, 10) || 1) : Math.min(activity.totalLevels, getHighestLevel(activityId) + 1);

    // Telemetria (P-166 F1): o router cronometra o nível e reporta a conclusão
    // pelo SDK es-jogos. Logado, vira linha em jogo_tentativas; deslogado, o
    // servidor responde 401 e o SDK engole — jogar continua livre.
    this.nivelInicioMs = performance.now();
    this.current = activity.mount({
      root: this.root,
      level,
      audio: audioManager,
      publico: this.publico,
      onComplete: (lvl, score, extras?: ConclusaoExtras) => {
        recordCompletion(activity.id, lvl, score);
        const tempoMs = Math.round(performance.now() - this.nivelInicioMs);
        this.nivelInicioMs = performance.now(); // "Próximo nível" não remonta
        const conclusao = {
          jogo: 'atividades',
          atividade: activity.id,
          nivel: lvl,
          score,
          score_max: extras?.total ?? 0,
          tentativas: extras?.tentativas ?? 0,
          erros: extras?.erros ?? 0,
          tempo_ms: tempoMs,
          publico: this.publico,
          params: extras?.params,
        };
        reportar(conclusao);
        ponte.concluido(conclusao);
      },
      onExit: () => {
        // Embed de atividade única: o hub dentro de um iframe pequeno é um
        // beco (ficou ilegível na Atividade do dia do Bit). Avisa o pai e
        // deixa ELE decidir — fechar, voltar pra salinha, trocar de treino.
        if (isEmbed) {
          ponte.sair('atividades');
          return;
        }
        this.goHome();
      },
    });
  }
}

export const router = new Router();
